import type { BillingPeriod as BillingPeriodType, PlanTier } from '@/lib/billing'

export type BillingPeriod = BillingPeriodType

export type MarketingPlan = {
  name: string
  monthlyPrice: number | null
  yearlyPrice: number | null
  description: string
  features: string[]
  cta: string
  planTier: PlanTier
  popular: boolean
  isContactSales?: boolean
  conversationLimit?: number | null
}

export const PRICING_DEFAULT_PERIOD: BillingPeriod = 'yearly'

export const PRICING_PLANS: MarketingPlan[] = [
  {
    name: 'Básico',
    monthlyPrice: 49,
    yearlyPrice: 39,
    description: 'Para equipos que quieren automatizar WhatsApp con límites claros.',
    features: [
      '4,000 mensajes incluidos/mes',
      '400 minutos de audio/mes',
      'Modelos de IA de última generación',
      'Soporte estándar',
    ],
    cta: 'Empezar prueba gratis',
    planTier: 'basic',
    popular: false,
    conversationLimit: 4000,
  },
  {
    name: 'Pro',
    monthlyPrice: 89,
    yearlyPrice: 71,
    description: 'Para equipos que necesitan más volumen y velocidad.',
    features: [
      '10,000 mensajes incluidos/mes',
      '1,000 minutos de audio/mes',
      'Modelos de IA ultrarrápidos',
      'Agenda con Google Calendar',
      'Soporte prioritario',
      'Analytics avanzados',
    ],
    cta: 'Empezar prueba gratis',
    planTier: 'professional',
    popular: true,
    conversationLimit: 10000,
  },
  {
    name: 'Enterprise',
    monthlyPrice: null,
    yearlyPrice: null,
    description: 'Para empresas con necesidades a medida.',
    features: [
      'Conversaciones ilimitadas',
      'Números ilimitados',
      'SLA garantizado',
      'Gerente de cuenta dedicado',
      'API completo',
    ],
    cta: 'Contactar',
    planTier: 'enterprise',
    popular: false,
    isContactSales: true,
    conversationLimit: null,
  },
]

export const getMarketingPlanByTier = (tier: PlanTier): MarketingPlan | null =>
  PRICING_PLANS.find((plan) => plan.planTier === tier) ?? null

export const getPlanPrice = (plan: MarketingPlan | null, period: BillingPeriod): number | null => {
  if (!plan) return null
  return period === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice
}

export const formatPlanPrice = (value: number | null): string =>
  value === null ? 'A medida' : `$${value}`

export const formatBillingPeriodLabel = (period: BillingPeriod): string =>
  period === 'yearly' ? 'anual' : 'mensual'

const basicPlan = getMarketingPlanByTier('basic')
const professionalPlan = getMarketingPlanByTier('professional')

const STARTING_PRICE_PERIOD: BillingPeriod = PRICING_DEFAULT_PERIOD
export const STARTING_PRICE_VALUE =
  getPlanPrice(basicPlan, STARTING_PRICE_PERIOD) ??
  basicPlan?.monthlyPrice ??
  basicPlan?.yearlyPrice ??
  0

export const STARTING_PRICE_TEXT = STARTING_PRICE_VALUE
  ? `$${STARTING_PRICE_VALUE}/mes`
  : 'Precio a medida'

export const STARTING_PRICE_NOTE = STARTING_PRICE_PERIOD === 'yearly' ? 'facturado anual' : ''

export const ROI_CONVERSATION_LIMITS = {
  basic: basicPlan?.conversationLimit ?? 1000,
  professional: professionalPlan?.conversationLimit ?? 10000,
}
